import { Observable, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { selectFlightType } from '@shared/base-search-bar/store/selectors/flight-type.selector';
import { FlightType } from '@search-bar/entities/flight-type';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { selectPassengerArrangement } from '@shared/base-search-bar/store/selectors/passenger-arrangement.selector';
import { selectFlightClass } from '@shared/base-search-bar/store/selectors/flight-class.selector';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { selectDefaultPlace } from '@shared/base-search-bar/store/selectors/default-place.selector';
import { State } from '@app/store/state/state';
import { selectDefaultAirport } from '@shared/base-search-bar/store/selectors/default-airport.selector';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { getUIsearchBarSelector } from '@shared/base-search-bar/store/selectors/ui.selector';
import { PassengerArrangementState } from '@shared/base-search-bar/entities/passenger-arrangement-state';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { SelectAutocompletePrediction } from '@search-bar/entities/select-autocomplete-prediction';
import { SearchType } from '@search-bar/entities/search-type';
import {
  selectArrivalPlace, selectDeparturePlace, selectDestinationPlace
} from '@shared/base-search-bar/store/selectors/select-place.selector';
import { selectArrivalDate, selectDepartureDate } from '@shared/base-search-bar/store/selectors/select-flight-calendar.selector';
import { searchBarSelector } from '@shared/base-search-bar/store/selectors/search-bar.selector';
import { searchBarFeatureName } from '@shared/base-search-bar/store/state/search-bar.state';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { selectAirline } from '@shared/base-search-bar/store/selectors/airline.selector';
import { GuestsPerRoomState } from '@shared/base-search-bar/store/reducers/guests-per-room.reducer';
import { selectGuestsPerRoom } from '@shared/base-search-bar/store/selectors/guests-per-room.selector';
import { selectDepartureTerminal } from '@shared/base-search-bar/store/selectors/departure-terminal.selector';
import { selectArrivalTerminal } from '@shared/base-search-bar/store/selectors/arrival-terminal.selector';
import { selectDropOffPlace } from '@shared/base-search-bar/store/selectors/dropoff-place.selector';
import { selectPickUpPlace } from '@shared/base-search-bar/store/selectors/pickup-place.selector';
import { selectDropOffTime, selectPickUpTime } from '@shared/base-search-bar/store/selectors/select-time.selector';
import { selectHourType } from '@shared/base-search-bar/store/selectors/hours-type.selector';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { AutoCompleteDestination, InputDestinationPlace } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { AutoCompleteTravelExtra, InputTravelExtra } from '@shared/base-search-bar/entities/autocomplete-travel-extra';

export class BaseSearchBarFacadeAbstract {

  private readonly forceCallWithoutParam$ = new Subject<void>();

  constructor(protected store: Store<State>) { }

  public defaultPlace$: Observable<SelectAutocompletePlace> = this.store.pipe(select(selectDefaultPlace));
  public departurePlace$: Observable<AutocompletePlace> = this.store.pipe(select(selectDeparturePlace));
  public arrivalPlace$: Observable<AutocompletePlace> = this.store.pipe(select(selectArrivalPlace));
  public departureTerminal$: Observable<AutocompletePlace> = this.store.pipe(select(selectDepartureTerminal));
  public arrivalTerminal$: Observable<AutocompletePlace> = this.store.pipe(select(selectArrivalTerminal));
  public destinationPlace$: Observable<AutocompletePlace> = this.store.pipe(select(selectDestinationPlace));
  public defaultAirport$: Observable<SelectAutocompletePlace> = this.store.pipe(select(selectDefaultAirport));
  public pickUpTime$: Observable<string> = this.store.pipe(select(selectPickUpTime));
  public dropOffTime$: Observable<string> = this.store.pipe(select(selectDropOffTime));
  public departureDate$: Observable<Date> = this.store.pipe(select(selectDepartureDate));
  public arrivalDate$: Observable<Date> = this.store.pipe(select(selectArrivalDate));
  public flightType$: Observable<FlightType> = this.store.pipe(select(selectFlightType));
  public flightClass$: Observable<FlightClass> = this.store.pipe(select(selectFlightClass));
  public hourType$: Observable<string[]> = this.store.pipe(select(selectHourType));
  public airline$: Observable<string> = this.store.pipe(select(selectAirline));
  public passengerArrangements$: Observable<PassengerArrangement[]> = this.store.pipe(select(selectPassengerArrangement));
  public guestsPerRoom$: Observable<Room[]> = this.store.pipe(select(selectGuestsPerRoom));
  public pickUpPlace$: Observable<SelectAutocompletePrediction> = this.store.pipe(select(selectPickUpPlace));
  public dropOffPlace$: Observable<SelectAutocompletePrediction> = this.store.pipe(select(selectDropOffPlace));
  public hotelDestinationPlace$: Observable<InputDestinationPlace> = this.store.pipe(select(searchBarSelector),
    map(state => state.hotelDestinationPlace));
  public packageDestinationPlace$: Observable<InputDestinationPlace> = this.store.pipe(select(searchBarSelector),
    map(state => state.packageDestinationPlace));
  public packageOriginPlace$: Observable<InputDestinationPlace> = this.store.pipe(select(searchBarSelector),
    map(state => state.packageOriginPlace));
  public inputTravelExtra$: Observable<InputTravelExtra> = this.store.pipe(select(searchBarSelector),
    map(state => state.inputTravelExtra));

  public packageStep$: Observable<PackageStep> = this.store.pipe(select(searchBarSelector), map(state => state.packageStep));
  public passengerArrangementsState$: Observable<PassengerArrangementState> = this.store.pipe(
    select(store => store[searchBarFeatureName].passengerArrangement));
  public guestsPerRoomState$: Observable<GuestsPerRoomState> = this.store.pipe(
    select(store => store[searchBarFeatureName].guestsPerRoom));
  public isLoadingAutocompletePlaces$: Observable<boolean> = this.store.pipe(
    select(getUIsearchBarSelector), map(state => state.isLoadingAutocompletePlaces));
  public isSearchAirportForIp$: Observable<boolean> = this.store.pipe(
    select(getUIsearchBarSelector), map(state => state.isSearchAirportForIp));
  public isSearchTerminalForIp$: Observable<boolean> = this.store.pipe(
    select(getUIsearchBarSelector), map(state => state.isSearchTerminalForIp));
  public placeTypeSelected$: Observable<PlaceType> = this.store.pipe(
    select(getUIsearchBarSelector), map(state => state.placeTypeSelected));
  public isHeaderEditButtonVisible$: Observable<boolean> = this.store.pipe(
    select(getUIsearchBarSelector), map(state => state.isHeaderEditButtonVisible));
  public skipServices$: Observable<boolean> = this.store.pipe(
    select(getUIsearchBarSelector), map(state => state.skipServices));
  public selectedTabHome$: Observable<SearchType> = this.store.pipe(
    select(getUIsearchBarSelector), map(state => state.tabSelectedHome));

  public autoCompletePlaces$: Observable<AutocompletePlaces> = this.store.pipe(
    select(searchBarSelector),
    map(state => state.autocompletePlaces)
  );

  public autocompleteDestinationPlaces$: Observable<AutoCompleteDestination> = this.store.pipe(
    select(searchBarSelector),
    map(state => state.autocompleteDestinationPlaces)
  );

  public autocompleteTravelExtra$: Observable<AutoCompleteTravelExtra> = this.store.pipe(
    select(searchBarSelector),
    map(state => state.autocompleteExtra)
  );

  public baggageIncluded$: Observable<boolean> = this.store.pipe(
    select(searchBarSelector),
    map(state => state.baggageIncluded)
  );

  public branchCode$: Observable<string> = this.store.pipe(
    select(searchBarSelector),
    map(state => state.branchCode)
  );

  public salesChannel$: Observable<string> = this.store.pipe(
    select(searchBarSelector),
    map(state => state.salesChannel)
  );

  public autoDiffPlace$: Observable<boolean> = this.store.pipe(
    select(getUIsearchBarSelector), map(state => state.autoDiffPlace)
  );

  public forceCallWithoutParam(): void {
    this.forceCallWithoutParam$.next();
  }

  public getforceCallWithoutParam(): Observable<void> {
    return this.forceCallWithoutParam$.asObservable();
  }
}
